import axios, { AxiosHeaders } from 'axios';
import { isUnauthorizedError } from '@/lib/api-errors';
import { notifySessionExpired } from '@/lib/session-expiry';
import { CSRF_HEADER_NAME, getCsrfToken } from './csrf';

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

/**
 * Pages a signed-out person is meant to reach by link. A 401 here is an
 * invalid or spent link, not a session that ran out, and telling the patient
 * their session expired would be nonsense — they never had one.
 */
const PUBLIC_PATHNAMES = ['/patients/test/answer', '/patients/survey/answer'];

/** Loosely-typed shape for axios-style errors surfaced from apiClient. */
export interface ApiError {
    status?: number;
    message?: string;
    response?: {
        status?: number;
        data?: { message?: string; [key: string]: unknown };
    };
}

export const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

apiClient.interceptors.request.use((config) => {
    const method = config.method?.toLowerCase();
    const csrfToken = getCsrfToken();

    if (method && MUTATING_METHODS.has(method) && csrfToken) {
        const headers = AxiosHeaders.from(config.headers);
        headers.set(CSRF_HEADER_NAME, csrfToken);
        config.headers = headers;
    }

    return config;
});

const shouldReportSessionExpiry = (error: unknown): boolean => {
    if (!isUnauthorizedError(error)) return false;

    // `/auth/bootstrap` answers 401 for every signed-out visitor, and it is the
    // call that decides someone is anonymous in the first place — it has its
    // own handling in `AuthProvider`.
    const requestUrl = String((error as { config?: { url?: string } }).config?.url ?? '');
    if (requestUrl.includes('/auth/')) return false;

    return !PUBLIC_PATHNAMES.some((pathname) => window.location.pathname.startsWith(pathname));
};

apiClient.interceptors.response.use(
    (response) => response.data,
    (error) => {
        if (shouldReportSessionExpiry(error)) {
            notifySessionExpired();
        }

        return Promise.reject(error);
    },
);
